import React, { useState } from "react";
import Icons from "../icons";

/* PARKED NAV ENTRIES — Users, Application Configuration, Scheduling,
 * Reports & Analytics.
 *
 * These four are commented out below rather than deleted, because they are
 * coming back. Nothing else was touched: MainPage still imports UsersView,
 * AppConfigView, SchedulingView and ReportsView, still passes onSelectUsers /
 * onSelectAppConfig / onSelectScheduling / onSelectReports, and still renders
 * their routes. The screens work in full — they just have no entry point right
 * now.
 *
 * To restore one, uncomment two things: its `is…Section` const in this file,
 * and its <button> block in the nav list. Both are marked RESTORE.
 *
 * Note: commented JSX is not compiled or linted, so it can drift out of date
 * (a renamed icon or handler will not raise an error until it is uncommented).
 * If one of these has been parked for a while, build once straight after
 * uncommenting it.
 */

/**
 * Left navigation rail.
 * Props:
 *  - active: current section id ("boards")
 *  - boards: array of boards to list as submenu items
 *  - subsByBoard: { [boardId]: [subcategory] } for nesting under each board
 *  - activeBoardId: currently opened board id (for highlight)
 *  - activeSubKey: currently opened sub-category key (for highlight)
 *  - onSelectBoards: show the boards list (parent click)
 *  - onOpenBoard(id): open a specific board (submenu click)
 *  - onOpenSubcategory(boardId, subKey): open a board drilled into a sub-category
 */
export default function Sidebar({
  active = "boards",
  boards = [],
  subsByBoard = {},
  activeBoardId = null,
  activeSubKey = null,
  onSelectDashboard,
  onSelectForYou,
  onSelectBoards,
  onOpenBoard,
  onOpenSubcategory,
  onSelectUsers,
  onSelectAppConfig,
  onSelectDatasource,
  onSelectReports,
  onSelectScheduling,
  onSelectTickets,
  onSelectRequirements,
  onSelectAppPage,
  onSelectAppPageExecutions,
  onSelectExecutions,
}) {
  const [open, setOpen] = useState(true);
  const isDashboardSection = active === "dashboard";
  const isForYouSection = active === "foryou";
  const isBoardsSection = active === "boards";
  // RESTORE (Users): uncomment with its button block below.
  // const isUsersSection = active === "users";
  // RESTORE (Application Configuration): uncomment with its button block below.
  // const isAppConfigSection = active === "appconfig";
  // RESTORE (Datasource): uncomment with its button block below.
  // const isDatasourceSection = active === "datasource";
  // RESTORE (Reports & Analytics): uncomment with its button block below.
  // const isReportsSection = active === "reports";
  // RESTORE (Scheduling): uncomment with its button block below.
  // const isSchedulingSection = active === "scheduling";
  // RESTORE (Jira Work Items): uncomment with its button block below.
  // const isTicketsSection = active === "tickets";
  const isRequirementsSection = active === "requirements";
  const isAppPageSection = active === "apppage";
  const isAppPageExecSection = active === "apppageExecutions";
  const isExecutionsSection = active === "executions";

  const handleParentClick = () => {
    setOpen((o) => !o);
    onSelectBoards?.();
  };

  return (
    <aside className="flex w-60 flex-none flex-col border-r border-slate-200 bg-card">
      <div className="cq-scroll flex-1 overflow-y-auto p-3">
        <p className="px-3 pb-2 pt-3 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          Workspace
        </p>

        <nav className="space-y-1">
          {/* Dashboard */}
          <button
            type="button"
            onClick={() => onSelectDashboard?.()}
            className={[
              "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition",
              isDashboardSection
                ? "bg-brand-50 text-brand-800 ring-1 ring-brand-600/20"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
            ].join(" ")}
          >
            <Icons.Home
              className={["h-5 w-5", isDashboardSection ? "text-brand-700" : "text-slate-400"].join(" ")}
            />
            <span className="flex-1 text-left">Dashboard</span>
          </button>

          {/* For you — epics, stories, tests and Test Executions assigned to the
              signed-in user in Jira */}
          <button
            type="button"
            onClick={() => onSelectForYou?.()}
            className={[
              "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition",
              isForYouSection
                ? "bg-brand-50 text-brand-800 ring-1 ring-brand-600/20"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
            ].join(" ")}
          >
            <Icons.UserCircle
              className={["h-5 w-5", isForYouSection ? "text-brand-700" : "text-slate-400"].join(" ")}
            />
            <span className="flex-1 text-left">For you</span>
          </button>

          {/* Requirement Board — epics › story cards (testing) › Xray & Playwright tests */}
          <button
            type="button"
            onClick={() => onSelectRequirements?.()}
            className={[
              "mt-2 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition",
              isRequirementsSection
                ? "bg-brand-50 text-brand-800 ring-1 ring-brand-600/20"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
            ].join(" ")}
          >
            <Icons.Layers
              className={["h-5 w-5", isRequirementsSection ? "text-brand-700" : "text-slate-400"].join(" ")}
            />
            <span className="flex-1 text-left">Requirement Board</span>
          </button>

          {/* RESTORE (Users) — parked, not deleted. Uncomment this block and the
              isUsersSection const near the top of this file.
          <button
            type="button"
            onClick={() => onSelectUsers?.()}
            className={[
              "mt-2 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition",
              isUsersSection
                ? "bg-brand-50 text-brand-800 ring-1 ring-brand-600/20"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
            ].join(" ")}
          >
            <Icons.Users
              className={["h-5 w-5", isUsersSection ? "text-brand-700" : "text-slate-400"].join(" ")}
            />
            <span className="flex-1 text-left">Users</span>
          </button>
          */}

          {/* RESTORE (Application Configuration) — parked, not deleted. Uncomment
              this block and the isAppConfigSection const near the top of this file.
          <button
            type="button"
            onClick={() => onSelectAppConfig?.()}
            className={[
              "mt-2 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition",
              isAppConfigSection
                ? "bg-brand-50 text-brand-800 ring-1 ring-brand-600/20"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
            ].join(" ")}
          >
            <Icons.Settings
              className={["h-5 w-5", isAppConfigSection ? "text-brand-700" : "text-slate-400"].join(" ")}
            />
            <span className="flex-1 text-left">Application Configuration</span>
          </button>
          */}

          {/* RESTORE (Datasource) — JSON datasets that feed dynamic test-case
              values. Parked, not deleted. Uncomment this block and the
              isDatasourceSection const near the top of this file.
          <button
            type="button"
            onClick={() => onSelectDatasource?.()}
            className={[
              "mt-2 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition",
              isDatasourceSection
                ? "bg-brand-50 text-brand-800 ring-1 ring-brand-600/20"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
            ].join(" ")}
          >
            <Icons.Grid
              className={["h-5 w-5", isDatasourceSection ? "text-brand-700" : "text-slate-400"].join(" ")}
            />
            <span className="flex-1 text-left">Datasource</span>
          </button>
          */}

          {/* RESTORE (Scheduling) — automated one-off / recurring test execution.
              Parked, not deleted. Uncomment this block and the isSchedulingSection
              const near the top of this file.
          <button
            type="button"
            onClick={() => onSelectScheduling?.()}
            className={[
              "mt-2 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition",
              isSchedulingSection
                ? "bg-brand-50 text-brand-800 ring-1 ring-brand-600/20"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
            ].join(" ")}
          >
            <Icons.Calendar
              className={["h-5 w-5", isSchedulingSection ? "text-brand-700" : "text-slate-400"].join(" ")}
            />
            <span className="flex-1 text-left">Scheduling</span>
          </button>
          */}

          {/* RESTORE (Jira Work Items) — browse issues from connected Atlassian
              Jira projects. Parked, not deleted. Uncomment this block and the
              isTicketsSection const near the top of this file.
          <button
            type="button"
            onClick={() => onSelectTickets?.()}
            className={[
              "mt-2 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition",
              isTicketsSection
                ? "bg-brand-50 text-brand-800 ring-1 ring-brand-600/20"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
            ].join(" ")}
          >
            <Icons.List
              className={["h-5 w-5", isTicketsSection ? "text-brand-700" : "text-slate-400"].join(" ")}
            />
            <span className="flex-1 text-left">Jira Work Items</span>
          </button>
          */}

          {/* Artifacts — epic → test → its executions (the Executions view) */}
          <button
            type="button"
            onClick={() => onSelectExecutions?.()}
            className={[
              "mt-2 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition",
              isExecutionsSection
                ? "bg-brand-50 text-brand-800 ring-1 ring-brand-600/20"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
            ].join(" ")}
          >
            <Icons.ScrollText
              className={["h-5 w-5", isExecutionsSection ? "text-brand-700" : "text-slate-400"].join(" ")}
            />
            <span className="flex-1 text-left">Artifacts</span>
          </button>

          {/* App Page — Cucumber scenarios generated from a dynamic App Page */}
          <button
            type="button"
            onClick={() => onSelectAppPage?.()}
            className={[
              "mt-2 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition",
              isAppPageSection
                ? "bg-brand-50 text-brand-800 ring-1 ring-brand-600/20"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
            ].join(" ")}
          >
            <Icons.Grid
              className={["h-5 w-5", isAppPageSection ? "text-brand-700" : "text-slate-400"].join(" ")}
            />
            <span className="flex-1 text-left">App Pages</span>
          </button>
          {/* App Page › Executions — the App Page's Artifacts and their runs */}
          <div className="ml-5 mt-1 border-l border-slate-200 pl-2">
            <button
              type="button"
              onClick={() => onSelectAppPageExecutions?.()}
              className={[
                "flex w-full items-center gap-2.5 rounded-lg px-3 py-1.5 text-[13px] font-medium transition",
                isAppPageExecSection
                  ? "bg-brand-50 text-brand-800 ring-1 ring-brand-600/20"
                  : "text-slate-500 hover:bg-slate-50 hover:text-slate-900",
              ].join(" ")}
            >
              <Icons.Play
                className={["h-4 w-4", isAppPageExecSection ? "text-brand-700" : "text-slate-400"].join(" ")}
              />
              <span className="flex-1 text-left">Executions</span>
            </button>
          </div>


          {/* RESTORE (Reports & Analytics) — build a report from a hand-picked
              set of test cases. Parked, not deleted. Uncomment this block and the
              isReportsSection const near the top of this file.

              Belongs last when restored: it reads the work the entries above
              produce, and sitting below the Boards tree keeps that order intact.
          <button
            type="button"
            onClick={() => onSelectReports?.()}
            className={[
              "mt-2 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition",
              isReportsSection
                ? "bg-brand-50 text-brand-800 ring-1 ring-brand-600/20"
                : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
            ].join(" ")}
          >
            <Icons.Chart
              className={["h-5 w-5", isReportsSection ? "text-brand-700" : "text-slate-400"].join(" ")}
            />
            <span className="flex-1 text-left">Reports &amp; Analytics</span>
          </button>
          */}
        </nav>
      </div>

      <div className="flex-none border-t border-slate-200 px-4 py-3">
        <div className="flex items-center gap-2 text-xs text-slate-400">
          <Icons.Spark className="h-4 w-4 text-brand-600" />
          CortexQA · v0.1
        </div>
      </div>
    </aside>
  );
}
